/**
 * Alert WebSocket Service
 * Handles real-time alert messages and dispatches to Redux store
 */

import { AppDispatch } from '@/store/index';
import { WebSocketMessage } from './types';
import { WebSocketMessageRouter } from './messageRouter';
import { addNotification } from '@/store/slices/uiSlice';

export class AlertWebSocketService {
  readonly name = 'alert-service';
  readonly version = '1.0.0';
  readonly dependencies: string[] = [];

  private router: WebSocketMessageRouter | null = null;
  private dispatch: AppDispatch | null = null;

  async initialize(router: WebSocketMessageRouter, dispatch: AppDispatch): Promise<void> {
    this.router = router;
    this.dispatch = dispatch;

    // High priority — alerts should process before device updates
    const messageTypes = this.getMessageTypes();
    for (const type of messageTypes) {
      router.registerHandler({
        messageType: type,
        handler: this.handleAlert.bind(this),
        priority: 10,
        service: this.name,
      });
    }
  }

  async cleanup(): Promise<void> {
    if (this.router) {
      for (const type of this.getMessageTypes()) {
        this.router.unregisterHandler(type, this.name);
      }
    }
    this.router = null;
    this.dispatch = null;
  }

  getMessageTypes(): string[] {
    return ['alert', 'alert_acknowledged', 'alert_resolved', 'alert_escalation'];
  }

  getHandlerPriority(): number {
    return 10;
  }

  getServiceInfo() {
    return {
      name: this.name,
      description: 'Handles real-time alert notifications',
      version: this.version,
      author: 'Saqia',
      supportedMessageTypes: this.getMessageTypes(),
      dependencies: this.dependencies,
      priority: this.getHandlerPriority(),
    };
  }

  private handleAlert(message: WebSocketMessage): void {
    if (!this.dispatch) return;

    const payload = message.data?.payload || message.data || {};
    const alertType = payload.alert_type || 'system';
    const severity = payload.severity || 'info';

    // Map severity to notification type
    const typeMap: Record<string, 'error' | 'warning' | 'info' | 'success'> = {
      critical: 'error',
      warning: 'warning',
      info: 'info',
    };

    this.dispatch(
      addNotification({
        type: typeMap[severity] || 'info',
        title: payload.title || payload.alert_title || 'Alert',
        message: payload.message || '',
        timestamp: new Date().toISOString(),
        alertId: payload.alert_id,
        alertType,
        severity,
        sourceType: payload.source_type,
        sourceName: payload.source_name,
      })
    );
  }
}

// Singleton instance
export const alertWebSocketService = new AlertWebSocketService();
